// ============================================================
// USER MANAGEMENT PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { UserPlus, Download, RefreshCw } from 'lucide-react';
import {
  Table, Button, SearchInput, Pagination,
  Modal, Avatar,
} from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getUsers } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate, formatDateTime, formatCurrency, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  finance_manager: 'Finance Manager',
  support_executive: 'Support Executive',
  operations_manager: 'Operations Manager',
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

const UserManagement = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [addSaving, setAddSaving] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: '', username: '', email: '', password: '', admin_role: 'admin' });

  useEffect(() => {
    adminApi.getAdminUsers(getUsers)
      .then(res => { setAllUsers(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const handleAddUser = async () => {
    if (!newUser.full_name || !newUser.username) {
      toast.error('Full name and username are required');
      return;
    }
    setAddSaving(true);
    try {
      const created = await adminApi.createAdminUser({
        full_name: newUser.full_name,
        username: newUser.username,
        email: newUser.email,
        password: newUser.password || 'Admin@123',
        admin_role: newUser.admin_role,
      });
      setAllUsers(p => [created, ...p]);
      setNewUser({ full_name: '', username: '', email: '', password: '', admin_role: 'admin' });
      setShowAdd(false);
      toast.success('Admin user created successfully');
    } catch (e) {
      toast.error(e.message || 'Failed to create user');
    } finally {
      setAddSaving(false);
    }
  };

  const { query, setQuery, filtered: searched } = useSearch(allUsers, ['full_name', 'username', 'email', 'admin_role']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(searched);

  const columns = [
    {
      key: 'full_name', label: 'User',
      render: (v, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar name={v || row.username} />
          <div>
            <div style={{ fontWeight: 600 }}>{v || row.username}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>@{row.username}</div>
            <div style={{ fontSize: 12, color: 'var(--admin-text-muted)' }}>{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'admin_role', label: 'Role',
      render: v => <span style={{ fontSize: 12, fontWeight: 600, color: '#3B82F6' }}>{ROLE_LABELS[v] || v || '—'}</span>,
    },
    { key: 'created_at', label: 'Joined', render: v => formatDate(v) },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="info" size="sm" onClick={() => setSelected(row)}>View</Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Admin User Management</h1>
          <p className="admin-page-subtitle">
            Manage admin panel users — admins, finance managers, support executives &nbsp;·&nbsp;
            <strong>{allUsers.length}</strong> admin users
          </p>
        </div>
        <div className="admin-page-actions">
          <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(allUsers, 'users')}>Export</Button>
          <Button variant="primary" icon={<UserPlus size={14} />} onClick={() => setShowAdd(true)}>Add User</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="admin-summary-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Users', value: allUsers.length, color: '#3B82F6' },
        ].map((s, i) => (
          <div key={i} className="admin-summary-item">
            <div className="admin-summary-item-value" style={{ color: s.color }}>{s.value}</div>
            <div className="admin-summary-item-label">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search users..." />
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={() => setQuery('')}>Reset</Button>
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={searched.length} itemsPerPage={10} onPageChange={goTo} />
      </div>

      {/* View Modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title="User Details" size="md"
        footer={<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>}>
        {selected && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, padding: '16px', background: 'var(--admin-bg)', borderRadius: 10 }}>
              <Avatar name={selected.full_name || selected.username} size={56} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{selected.full_name || '—'}</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>@{selected.username}</div>
                <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>{selected.email}</div>
              </div>
            </div>
            {[
              ['User ID', selected.user_id || selected.id],
              ['Username', selected.username],
              ['Role', ROLE_LABELS[selected.admin_role] || selected.admin_role || '—'],
              ['Joined', formatDateTime(selected.created_at || selected.createdAt)],
              ['Last Login', formatDateTime(selected.last_login || selected.lastLogin)],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Add User Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add New Admin User" size="md"
        footer={<>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleAddUser} disabled={addSaving}>
            {addSaving ? 'Creating…' : 'Create User'}
          </Button>
        </>}>
        <div className="admin-grid-2">
          <div className="admin-form-group">
            <label>Full Name <span style={{ color: '#EF4444' }}>*</span></label>
            <input className="admin-form-control" placeholder="Enter full name"
              value={newUser.full_name} onChange={e => setNewUser(p => ({ ...p, full_name: e.target.value }))} />
          </div>
          <div className="admin-form-group">
            <label>Username <span style={{ color: '#EF4444' }}>*</span></label>
            <input className="admin-form-control" placeholder="Enter username (used for login)"
              value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} />
          </div>
          <div className="admin-form-group">
            <label>Email</label>
            <input className="admin-form-control" placeholder="Enter email address" type="email"
              value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
          </div>
          <div className="admin-form-group">
            <label>Password</label>
            <input className="admin-form-control" placeholder="Default: Admin@123" type="password"
              value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} />
          </div>
          <div className="admin-form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Role <span style={{ color: '#EF4444' }}>*</span></label>
            <select className="admin-form-control"
              value={newUser.admin_role} onChange={e => setNewUser(p => ({ ...p, admin_role: e.target.value }))}>
              {ROLE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--admin-text-muted)', marginTop: 12 }}>
          ℹ️ Leave password blank to use default <code>Admin@123</code>. Username is used for admin login.
        </p>
      </Modal>
    </div>
  );
};

export default UserManagement;
